import {
    Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel,
    Box,
    Button,
    Card, CardBody, CardHeader,
    Flex,
    Grid, GridItem,
    Heading,
    Icon,
    IconButton,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Select, Skeleton, Stack, SimpleGrid,
    Text, Textarea,
    useDisclosure,
    useToast,
    Badge,
    Divider
} from "@chakra-ui/react";
import { MdHistory, MdDelete, MdSave } from "react-icons/md";
import React, { useEffect, useState } from "react";
import { completeIEPStep, onGetHistory, onSaveAnswers, updateData } from "../../http/iepAPis";
import _groupBy from "lodash/groupBy";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CoustomAlertDialog from "../../components/CoustomAlertDialog";
import { canAddStudent, isAnswerContentGreaterThanTwo } from "../../utils/common";
import GoalsAndObjectivesView from "./GoalsAndObjectives";
import InlineLoader from "../../components/InlineLoader";

const ReviewObjectiveItem = ({ r, iepId, isCompleted, review, setReview, handleSave, onOpenReview, setGoalId, step, loading, t }) => {
    const isNeedsAttention = r.answer && parseInt(r.answer.content) <= 2;

    return (
        <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" mb={3} bg={isNeedsAttention ? "red.50" : "gray.50"} overflow="hidden">
            <h2>
                <AccordionButton _expanded={{ bg: isNeedsAttention ? 'red.100' : 'blue.50', color: isNeedsAttention ? 'red.800' : 'blue.800' }} py={4}>
                    <Box as="span" flex='1' textAlign='left' fontWeight="bold" fontSize="md">
                        {r.objective}
                    </Box>
                    {r.answer && (
                        <Badge colorScheme={parseInt(r.answer.content) > 2 ? 'green' : 'red'} mr={4} px={3} py={1} borderRadius="full">
                            Score: {r.answer.content}
                        </Badge>
                    )}
                    <AccordionIcon />
                </AccordionButton>
            </h2>
            <AccordionPanel pb={6} pt={4} bg="white">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500" textTransform="uppercase">Activities</Text>
                        <Text p={3} bg="blue.50" borderRadius="xl" fontSize="md" color="blue.900" border="1px solid" borderColor="blue.100">{r.activities}</Text>
                    </Box>
                    <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500" textTransform="uppercase">Materials</Text>
                        <Text p={3} bg="teal.50" borderRadius="xl" fontSize="md" color="teal.900" border="1px solid" borderColor="teal.100">{r.material}</Text>
                    </Box>
                    <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500" textTransform="uppercase">Review (Grade/Score)</Text>
                        <Select
                            variant="filled"
                            size="lg"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            isDisabled={isCompleted || !canAddStudent()}
                            _disabled={{ background: 'gray.50', color: 'gray.800', opacity: 1, cursor: 'not-allowed', borderColor: 'gray.300' }}
                            value={review.get(r.questionId) ? review.get(r.questionId).content[0] : ''}
                            onChange={(e) => {
                                let answer = review.get(r.questionId) || {
                                    questionId: r.questionId,
                                    iepId: iepId,
                                    type: 'MULTI_CHOICE',
                                    content: [],
                                    remarks: ''
                                };
                                answer.content = [e.target.value];
                                setReview((prev) => new Map(prev).set(r.questionId, answer));
                            }}
                        >
                            {r.options.map((q) => <option key={q} value={q}>{q}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.500" textTransform="uppercase">Comments</Text>
                        <Textarea
                            variant="filled"
                            size="lg"
                            minH="120px"
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            isDisabled={isCompleted || !canAddStudent()}
                            _disabled={{ background: 'gray.50', color: 'gray.800', opacity: 1, cursor: 'not-allowed', borderColor: 'gray.300' }}
                            value={review.get(r.questionId) ? review.get(r.questionId).remarks : ''}
                            onChange={(e) => {
                                let answer = review.get(r.questionId) || {
                                    questionId: r.questionId,
                                    iepId: iepId,
                                    type: 'MULTI_CHOICE',
                                    content: [],
                                    remarks: ''
                                };
                                answer.remarks = e.target.value;
                                setReview((prev) => new Map(prev).set(r.questionId, answer));
                            }}
                        />
                    </Box>
                </SimpleGrid>

                <Flex mt={6} gap={4} justify="flex-end">
                    <Button
                        size="lg"
                        colorScheme="blue"
                        leftIcon={<Icon as={MdSave} />}
                        isLoading={loading}
                        onClick={() => handleSave(review.get(r.questionId))}
                        borderRadius="xl"
                        px={8}
                        boxShadow="md"
                        _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                    >
                        Save Progress
                    </Button>
                    {!isCompleted && r.answer && parseInt(r.answer.content) <= 2 && step !== 6 && (
                        <Button
                            size="lg"
                            colorScheme="orange"
                            variant="solid"
                            onClick={() => {
                                setGoalId(r.goalId);
                                onOpenReview();
                            }}
                            borderRadius="xl"
                            px={8}
                            boxShadow="md"
                            _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                        >
                            Review Objective
                        </Button>
                    )}
                </Flex>
            </AccordionPanel>
        </AccordionItem>
    );
};

export default function ReviewView({ title, subTitle, iepId, firstReview, secondReview, finalReview, onGetData, step, isCompleted, goalsAndObjectives, mainReview }) {
    const { t } = useTranslation('iep');
    const toast = useToast();
    const [historyRes, setHistoryRest] = useState([]);


    const [loading, setLoading] = useState(false);
    const [goalId, setGoalId] = useState(undefined);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const { isOpen: isOpenHistory, onOpen: onOpenHistory, onClose: onCloseHistory } = useDisclosure()
    const { isOpen: isOpenReview, onOpen: onOpenReview, onClose: onCloseReview } = useDisclosure()
    const [review, setReview] = useState(new Map());

    useEffect(() => {
        if (step === 4) {
            let fMap = new Map();
            firstReview.reviews.map((r) => {
                let answer = {
                    questionId: r.questionId,
                    iepId: iepId,
                    type: 'MULTI_CHOICE',
                    content: r.answer ? [r.answer.content] : [],
                    remarks: r.answer ? r.answer.remarks : ''
                }
                fMap.set(r.questionId, answer)
            })

            setReview(fMap);
        }
        if (step === 5) {
            let fMap = new Map();
            secondReview.reviews.map((r) => {
                let answer = {
                    questionId: r.questionId,
                    iepId: iepId,
                    type: 'MULTI_CHOICE',
                    content: r.answer ? [r.answer.content] : [],
                    remarks: r.answer ? r.answer.remarks : ''
                }
                fMap.set(r.questionId, answer)
            })

            setReview(fMap);
        }
        if (step === 6) {
            let fMap = new Map();
            finalReview.reviews.map((r) => {
                let answer = {
                    questionId: r.questionId,
                    iepId: iepId,
                    type: 'MULTI_CHOICE',
                    content: r.answer ? [r.answer.content] : [],
                    remarks: r.answer ? r.answer.remarks : ''
                }
                fMap.set(r.questionId, answer)
            })

            setReview(fMap);
        }

    }, [step])


    const btnRef = React.useRef(null)


    const handleSave = async (userAnswers) => {

        setLoading(true);

        try {
            await onSaveAnswers(userAnswers);
            onGetData();
            toast({
                title: 'Review Successful',
                description: "Review Submitted Successfully!!!",
                status: 'success',
                duration: 3000,
                isClosable: true,
            })
        } catch (e) {
            console.log(e)
            toast({
                title: 'Error!!',
                description: "Could not submit review!!",
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
        } finally {
            setLoading(false)
        }





    }
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const handleClose = () => {

        onClose();


    }
    const onCompleteIEP = async () => {
        try {
            setLoading(true)
            await completeIEPStep(iepId, step, !isCompleted);
            toast({
                title: 'Step Completed',
                description: "Review  Completed",
                status: 'success',
                duration: 3000,
                isClosable: true,
            })
            onGetData();
        } catch (e) {
            console.log(e)
            toast({
                title: 'Error',
                description: "Review Could not be completed !",
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
        } finally {
            setLoading(false);
            navigate(-1);
        }


    }

    const update = async (newObj) => {
        try {
            setLoadingHistory(true)
            if (step === 4) {

                for (let i = 0; i <= secondReview.reviews.length; i++) {
                    let data = secondReview.reviews[i];
                    if (data) {
                        const obj = {
                            "goalId": data.goalId,
                            "reviewQuestionId": data.questionId,
                            "goalQuestionContent": newObj.get(data.goalId) ? newObj.get(data.goalId).content : '',
                            "objectiveQuestionId": data.objectiveId,
                            "objectiveQuestionContent": newObj.get(data.objectiveId) ? newObj.get(data.objectiveId).content : '',
                            "activityQuestionId": data.activitiesId,
                            "activityQuestionContent": newObj.get(data.activitiesId) ? newObj.get(data.activitiesId).content : '',
                            "materialQuestionId": data.materialId,
                            "materialQuestionContent": newObj.get(data.materialId) ? newObj.get(data.materialId).content : '',
                            "responsibleQuestionId": data.responsibleQuestionId,
                            "responsibleQuestionContent": newObj.get(data.responsibleQuestionId) ? newObj.get(data.responsibleQuestionId).content : '',
                        }
                        await updateData(obj)
                    }
                }
            }
            if (step === 4 || step === 5) {
                for (let i = 0; i <= finalReview.reviews.length; i++) {
                    let data = finalReview.reviews[i];
                    if (data) {
                        const obj = {
                            "goalId": data.goalId,
                            "reviewQuestionId": data.questionId,
                            "goalQuestionContent": newObj.get(data.goalId) ? newObj.get(data.goalId).content : '',
                            "objectiveQuestionId": data.objectiveId,
                            "objectiveQuestionContent": newObj.get(data.objectiveId) ? newObj.get(data.objectiveId).content : '',
                            "activityQuestionId": data.activitiesId,
                            "activityQuestionContent": newObj.get(data.activitiesId) ? newObj.get(data.activitiesId).content : '',
                            "materialQuestionId": data.materialId,
                            "materialQuestionContent": newObj.get(data.materialId) ? newObj.get(data.materialId).content : '',
                            "responsibleQuestionId": data.responsibleQuestionId,
                            "responsibleQuestionContent": newObj.get(data.responsibleQuestionId) ? newObj.get(data.responsibleQuestionId).content : '',
                        }
                        await updateData(obj)
                    }
                }
            }
            onGetData();
            setLoadingHistory(false);
            onCloseReview();
        } catch (e) {
            toast({
                title: 'Error',
                description: e?.response?.data?.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
            setLoadingHistory(false);
            onCloseReview();
        }
    }
    const isAnyAnswerContentGreaterThanTwo = mainReview.reviews.some(item => isAnswerContentGreaterThanTwo(item));



    return (
        <Box mt={'24px'}>
            <Box mb={6} pb={4} borderBottom="1px solid" borderColor="gray.100">
                <Text fontWeight={'900'} fontSize={{ base: '20px', md: '24px' }} color="blue.600">
                    {title} : {subTitle}
                </Text>
            </Box>

            {firstReview && (
                Object.entries(_groupBy(mainReview.reviews, 'goal')).map(([key, value]) => (
                    <Card key={key} variant="outline" borderRadius="2xl" mb={8} boxShadow="sm" overflow="hidden" border="1px solid" borderColor="gray.100">
                        <CardHeader bg="blue.50" py={3}>
                            <Heading size="md" color="blue.700">{key}</Heading>
                        </CardHeader>

                        <CardBody p={4}>
                            <Accordion allowMultiple defaultIndex={[0]}>
                                {value.map((r) => (
                                    r.objective && r.objective !== '' && (
                                        <ReviewObjectiveItem
                                            key={r.questionId}
                                            r={r}
                                            iepId={iepId}
                                            isCompleted={isCompleted}
                                            review={review}
                                            setReview={setReview}
                                            handleSave={handleSave}
                                            onOpenReview={onOpenReview}
                                            setGoalId={setGoalId}
                                            step={step}
                                            loading={loading}
                                            t={t}
                                        />
                                    )
                                ))}
                            </Accordion>
                        </CardBody>
                    </Card>
                ))
            )}
            {isAnyAnswerContentGreaterThanTwo && (
                <Text fontWeight={500} color={'red.500'} fontStyle={'italic'} mt={'16px'}>
                    एक वा एकभन्दा बढि उद्देश्य⁄सिकाइ उपलब्धिको अङ्क १ वा २ मात्र आएको हुनाले पुनः योजना बनाइ थप क्रियाकलाप तथा सहयोग गर्नुपर्ने हुन्छ । अब
                    तपाईँले सबै सिकाई उपलब्धीहरू समिक्षा गर्नुपर्छ ।
                </Text>
            )}


            <Flex mt={'32px'} direction={{ base: "column", md: "row" }} gap={4} justifyContent={'flex-end'}>
                <Button variant="ghost" size='lg' isDisabled={loading} onClick={() => navigate(-1)} borderRadius="xl">
                    Back
                </Button>
                <Button
                    colorScheme={isCompleted ? 'orange' : 'green'}
                    size='lg'
                    onClick={() => onOpen()}
                    isDisabled={!canAddStudent()}
                    borderRadius="xl"
                    boxShadow="md"
                >
                    {isCompleted ? t('redoStep') : t('completeStep')}
                </Button>
            </Flex>
            <CoustomAlertDialog
                onClose={handleClose}
                onOpen={onOpen}
                isOpen={isOpen}
                onMainAction={onCompleteIEP}
                loading={loading}
                warning={true}
            />
            {isOpenHistory && (
                <Modal
                    onClose={onCloseHistory}
                    finalFocusRef={btnRef}
                    isOpen={isOpenHistory}
                    scrollBehavior={'inside'}
                    size={'6xl'}
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>History</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {loadingHistory ?
                                <Stack>
                                    <Skeleton height='20px' />
                                    <Skeleton height='20px' />
                                    <Skeleton height='20px' />
                                </Stack> :
                                <Box>
                                    {historyRes.map((i) =>
                                        <Box>
                                            <Text fontWeight={'900'} fontSize={'20px'} color={'#000000'}>{i.objective}</Text>
                                            <Text fontWeight={'500'} fontSize={'16px'} color={'#484545'}>{i.activities}</Text>
                                        </Box>

                                    )}
                                </Box>
                            }
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onCloseHistory}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
            {isOpenReview && (
                <Modal
                    onClose={onCloseReview}
                    finalFocusRef={btnRef}
                    isOpen={isOpenReview}
                    scrollBehavior={'inside'}
                    size={'6xl'}
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Review Goals And Objectives</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {loadingHistory ?
                                <Stack>
                                    <Skeleton height='20px' />
                                    <Skeleton height='20px' />
                                    <Skeleton height='20px' />
                                </Stack> :
                                <Box>
                                    <GoalsAndObjectivesView goalsAndObjectives={goalsAndObjectives}
                                        iepId={iepId} onGetData={onGetData}
                                        isCompleted={false}
                                        reviewStep={step}
                                        goalId={goalId}
                                        onUpdateObjetives={(data) => update(data)}
                                    />
                                </Box>
                            }
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}

        </Box>
    )
}
